import React from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck } from "lucide-react";
import { Product, formatPrice } from "@/components/product-step/types";
import { CurrencyAmount } from "@/components/CurrencyAmount";
import { useMarket } from "@/hooks/useMarket";
import { BookingLanguage } from "@/types/booking";
import { resolveProductKey } from "@/locales";
import { languageMap } from "@/i18n";

interface MembershipIncludedCardProps {
  product: Product;
  /** True when backed by an available entitlement → CHF 0 + "INCLUDED" label */
  isEntitled: boolean;
  /** Member price in cents (used when not entitled) */
  memberPrice: number;
  /** Follow-up counter badge, e.g. { current: 1, total: 3 }. Null = no badge. */
  counter: { current: number; total: number } | null;
  /** "X more follow ups coming with your membership". 0 = don't show. */
  remainingFollowUps: number;
  language: BookingLanguage;
  onSchedule: () => void;
}

export const MembershipIncludedCard: React.FC<MembershipIncludedCardProps> = ({
  product,
  isEntitled,
  memberPrice,
  counter,
  remainingFollowUps,
  language,
  onSchedule,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();

  const displayName =
    product.translations?.name || resolveProductKey(language, product.name);
  const description =
    product.translations?.description ||
    resolveProductKey(language, product.description);
  const productImage = product.images?.[0] ?? null;
  const durationText = product.duration_minutes
    ? `${t("packageCard.durationLabel")}: ${product.duration_minutes} min`
    : null;

  return (
    <div className="flex flex-col items-start w-full">
      {/* Card with blue border */}
      <div
        className="bg-white/80 rounded-2xl p-6 w-full"
        style={{
          boxShadow: "0px 0px 30px 0px #2D2D2D0A, 0px 16px 60px 0px #2D2D2D1F",
        }}
      >
        <div className="flex flex-col gap-4 w-full">
          {/* Header: image + info */}
          <div className="flex gap-6 items-start w-full">
            {/* Product image */}
            <div className="w-[78px] h-[78px] rounded-lg flex-shrink-0 overflow-hidden bg-neutral-100">
              {productImage ? (
                <img
                  src={productImage}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-neutral-200" />
              )}
            </div>

            {/* Text info */}
            <div className="flex flex-col flex-1 min-w-0">
              {/* Counter badge */}
              {counter && (
                <span className="text-xs font-medium uppercase tracking-wide text-aubergine-600 leading-[25px]">
                  {t("membership.followUpCheckIn", {
                    current: counter.current,
                    total: counter.total,
                  })}
                </span>
              )}

              {/* Product name */}
              <h3 className="text-[22px] font-medium leading-7 text-neutral-900">
                {displayName}
              </h3>

              {/* Duration */}
              {durationText && (
                <span className="text-sm text-neutral-500 leading-[25px]">
                  {durationText}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-base text-neutral-900 leading-6">
              {description}
            </p>
          )}

          {/* Price / Included label */}
          {isEntitled ? (
            <span className="text-xs font-medium uppercase tracking-wide text-deep-green-600 leading-8">
              {t("membership.included")}
            </span>
          ) : (
            <div className="flex items-baseline gap-1">
              <CurrencyAmount
                market={market}
                symbolClassName="text-sm text-neutral-500"
              >
                {product.default_price &&
                memberPrice < product.default_price.unit_amount ? (
                  <>
                    <span className="text-[22px] text-neutral-400 line-through">
                      {formatPrice(
                        product.default_price.unit_amount,
                        market,
                        true,
                      )}
                    </span>{" "}
                    <span className="text-[22px] font-bold text-neutral-900">
                      {formatPrice(memberPrice, market, true)}
                    </span>
                  </>
                ) : (
                  <span className="text-[22px] font-bold text-neutral-900">
                    {formatPrice(memberPrice, market, true)}
                  </span>
                )}
              </CurrencyAmount>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={onSchedule}
            className="w-full h-11 bg-aubergine-800 hover:brightness-[1.35] transition-all duration-100 rounded-full flex items-center justify-center gap-2 text-white font-medium text-base leading-6 tracking-[0.15px] normal-case"
          >
            <CalendarCheck className="w-5 h-5" />
            {t("membership.scheduleYourTest")}
          </button>
        </div>
      </div>

      {/* Remaining follow-ups text */}
      {counter && (
        <div className="w-full py-3 text-center">
          <span className="text-sm text-neutral-500">
            {remainingFollowUps > 0
              ? t("membership.moreFollowUps", { count: remainingFollowUps })
              : t("membership.lastFollowUp")}
          </span>
        </div>
      )}
    </div>
  );
};
